/**
 * `ColumnDef` list for the Processes table, matching the legacy warehouse
 * Processes grid: Process Code (link → detail) · Type (Chip) · Status
 * (StatusPill) · From (source) · To (destination) · Reason · Created.
 */
import type { ColumnDef } from '@vnetwork/ui/data';
import { Calendar } from 'lucide-react';
import type { ProcessEndpoint, WarehouseProcess } from '@/api/capacities';
import { TableLink } from '@/components/table-link';
import { endpointName, processStatusCell, processTypeCell } from '../process-format';
import { formatWhDateTime, formatWhRelative } from '../../shared/wh-format';

const muted = { color: 'var(--vn-fg-muted)' } as const;

/** Endpoint name cell — plain text, em dash fallback (legacy "N/A"/"-" slot). */
function endpointCell(endpoint?: ProcessEndpoint) {
  const name = endpointName(endpoint);
  return name ? (
    <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>{name}</span>
  ) : (
    <span style={muted}>—</span>
  );
}

export const processColumns: ColumnDef<WarehouseProcess, unknown>[] = [
  {
    id: 'code',
    header: 'Process Code',
    cell: ({ row }) => (
      <TableLink to={`/capacities/processes/${row.original.id}`} title={row.original.code}>
        {row.original.code || row.original.id}
      </TableLink>
    ),
  },
  {
    id: 'type',
    header: 'Type',
    cell: ({ row }) => processTypeCell(row.original.type),
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => processStatusCell(row.original.status),
  },
  {
    id: 'source',
    header: 'From',
    cell: ({ row }) => endpointCell(row.original.source),
  },
  {
    id: 'destination',
    header: 'To',
    cell: ({ row }) => endpointCell(row.original.destination),
  },
  {
    id: 'reason',
    header: 'Reason',
    cell: ({ row }) =>
      row.original.reason ? (
        <span
          title={row.original.reason}
          style={{
            fontSize: 'var(--vn-font-size-sm)',
            display: 'block',
            maxWidth: 220,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.original.reason}
        </span>
      ) : (
        <span style={muted}>—</span>
      ),
  },
  {
    id: 'created_at',
    header: 'Created',
    cell: ({ row }) =>
      row.original.created_at ? (
        <span
          title={formatWhDateTime(row.original.created_at)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--vn-space-1)',
            fontSize: 'var(--vn-font-size-sm)',
          }}
        >
          <Calendar size={14} style={{ color: 'var(--vn-fg-muted)', flexShrink: 0 }} />
          {formatWhRelative(row.original.created_at)}
        </span>
      ) : (
        <span style={muted}>—</span>
      ),
  },
];
